"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.checkoutTabSchema = exports.splitOrderBillSchema = exports.moveTabItemsSchema = exports.changeTabVisitorSchema = exports.ELECTRONIC_PROVIDER_RULE_MESSAGE = exports.PAYMENT_METHODS = void 0;
exports.electronicProviderRuleHolds = electronicProviderRuleHolds;
const zod_1 = require("zod");
/**
 * Tab checkout and the combined receipt — SPEC §5.3, §10.
 *
 * Checkout is the moment a party leaves: every running session on the tab is
 * closed, each becomes its own Reservation bill (which is why the Bills tab
 * shows one row per device), and the whole lot is rendered as ONE long receipt
 * grouped by device.
 */
/**
 * How a bill was paid. Mirrors the Prisma enum `PaymentMethod`.
 *
 * `ELECTRONIC` (2026-09-24) is money taken through a configured e-payment
 * provider — FIB, Qi, FastPay — on the provider's own terminal or app. It is
 * never cash: the box, the drawer count and the Dashboard's "cash boxed" keep
 * it apart exactly as they keep account balance apart, and every payment row
 * that carries it names its provider (`Payment.electronicsProviderId`).
 */
exports.PAYMENT_METHODS = ['CASH', 'ACCOUNT_BALANCE', 'ELECTRONIC'];
/**
 * The provider rule every tender schema shares: `electronicsProviderId` is
 * REQUIRED when the method is `ELECTRONIC` and REFUSED otherwise. Refused
 * rather than ignored, because a cash checkout carrying a provider id is a
 * till that thinks it took a card payment — better a 400 than a receipt that
 * says cash for money that went through FIB.
 */
function electronicProviderRuleHolds(value) {
    const hasProvider = typeof value.electronicsProviderId === 'string';
    return value.method === 'ELECTRONIC' ? hasProvider : !hasProvider;
}
exports.ELECTRONIC_PROVIDER_RULE_MESSAGE = 'Choose the payment provider for an electronic payment (and only then).';
/**
 * Ids in a partial settlement. Set semantics, because that is obviously what a
 * selection means — a repeated id would otherwise settle once and then refuse
 * itself with `session_already_settled`.
 *
 * THE CAP WAS 64, AND 64 WAS WRONG. It was written on the reasoning that "a
 * party is a few dozen devices, not thousands" — true of a party, and not true
 * of the thing venues actually keep open. On 2026-09-20 the owner could not
 * settle a house tab that had been open since 12 August carrying 342 unsettled
 * sittings: the till sent every id, zod answered "Too big", and all he saw was
 * "Some fields need fixing". It had been unsettleable at any discount since
 * partial settlement shipped.
 *
 * The cap was never bounding the work, either: OMITTING the selection settles
 * the whole tab through the same endpoint with no cap at all, so 342 sittings
 * in one transaction was always reachable — just not by naming them.
 *
 * 500 keeps a bound on a client that loops or lies, and clears the largest tab
 * this venue has produced by half again. The till now omits the selection
 * entirely when the ticked rows are the whole party (see `coversWholeTab`), so
 * this limit applies only to a genuine subset, which is what it was for.
 */
const selectionIds = zod_1.z
    .array(zod_1.z.string().min(1))
    .max(500)
    .transform((ids) => [...new Set(ids)]);
/**
 * Change visitor — move an open tab from one person to another (SPEC §5.3).
 *
 * The party was opened under the wrong name, or one of them takes the group over
 * and will be the one paying. EXACTLY one of the two identities is set, because
 * `visitorName` (walk-in) and `customerAccountId` (registered) are separate
 * columns precisely so the owner can tell at a glance which is which.
 *
 * A reason is mandatory. This changes who owes the money and, when the tab moves
 * onto a registered account, whose discounts and whose balance the remaining
 * checkout will use — the audit entry is the only control on that.
 */
exports.changeTabVisitorSchema = zod_1.z
    .object({
    /** Move the tab to a walk-in of this name. */
    visitorName: zod_1.z.string().trim().min(1).max(80).optional(),
    /** Move the tab onto a registered account. */
    customerAccountId: zod_1.z.string().min(1).optional(),
    reason: zod_1.z.string().trim().min(1, 'A reason is required.').max(300),
})
    .refine((value) => (value.visitorName === undefined) !== (value.customerAccountId === undefined), {
    message: 'Give either a visitor name or a customer account, not both.',
    path: ['visitorName'],
});
/**
 * Move items from one tab to another — how two parties merge, and how one splits
 * (SPEC §5.3).
 *
 * Two groups turn out to be one group, or a friend's drinks were rung up on the
 * wrong tab. The cashier picks what moves and whose tab it moves to, and only
 * UNPAID things can move: a settled bill has a printed receipt and money in the
 * box against a named person, and moving it would rewrite that.
 *
 * Moving everything is a merge; moving some of it is a split. There is no
 * separate "merge tabs" verb because there is no separate operation — merging is
 * just moving all of it, and a verb that could ONLY move all of it would be the
 * more dangerous of the two.
 */
exports.moveTabItemsSchema = zod_1.z
    .object({
    /** The tab everything is moving TO. Must be open, and not this one. */
    targetTabId: zod_1.z.string().min(1).optional(),
    /**
     * ...or a person who has no tab yet, which is opened for them.
     *
     * The common case at a counter is not "move these onto Ahmed's tab" but
     * "these are actually Ahmed's" — said about somebody who walked in with the
     * group and was never checked in separately. Without this the cashier has
     * to open a tab for them somewhere else first and come back, and the usual
     * outcome of that is the items staying on the wrong name.
     */
    targetVisitorName: zod_1.z.string().trim().min(1).max(120).optional(),
    sessionIds: selectionIds.default([]),
    orderBillIds: selectionIds.default([]),
    reason: zod_1.z.string().trim().min(1, 'A reason is required.').max(300),
})
    .refine((value) => value.sessionIds.length + value.orderBillIds.length > 0, {
    message: 'Select at least one device or order to move.',
    path: ['sessionIds'],
})
    .refine((value) => Boolean(value.targetTabId) !== Boolean(value.targetVisitorName), {
    // Exactly one. Both would leave the server choosing which the cashier meant,
    // and neither is a move with no destination.
    message: 'Choose an open tab or type a new name — not both.',
    path: ['targetTabId'],
});
/**
 * Split one OPEN order bill by item lines (SPEC §5.3 partial settlement, at
 * item grain): "he ordered a burger and a pepsi, and he is paying for the
 * burger only". The named lines move onto a NEW order with a NEW OPEN bill on
 * the same tab, and the existing per-bill checkout selection then settles that
 * bill like any other — the money code at the till is untouched.
 *
 * Nothing is charged by the split itself, so an abandoned checkout leaves two
 * OPEN bills that together owe exactly what the one did.
 */
exports.splitOrderBillSchema = zod_1.z.object({
    billId: zod_1.z.string().min(1),
    /** Set semantics, like every other selection — a repeated id means itself. */
    orderItemIds: zod_1.z
        .array(zod_1.z.string().min(1))
        .min(1, 'Select at least one item.')
        .max(64)
        .transform((ids) => [...new Set(ids)]),
});
/**
 * One bucket's discount at the till: a percent AND an amount, side by side
 * (owner decision).
 *
 * The percent is the standing arrangement — omit it and the customer's own saved
 * percent applies, which is what every checkout did before this existed. Give it
 * and it overrides for this checkout only; the account is never rewritten from
 * the till.
 *
 * The amount is what gets haggled at the counter. They add, and the total is
 * capped at that bucket's own subtotal — see `computeBucketDiscountsWithAmounts`.
 */
const bucketDiscountSchema = zod_1.z
    .object({
    /** Omitted means "use the customer's own percent"; 0 means "none today". */
    percent: zod_1.z.number().int().min(0).max(100).optional(),
    amountIqd: zod_1.z.number().int().min(0).max(1_000_000_000).default(0),
})
    .default({ amountIqd: 0 });
exports.checkoutTabSchema = zod_1.z
    .object({
    method: zod_1.z.enum(exports.PAYMENT_METHODS).default('CASH'),
    /**
     * The provider an `ELECTRONIC` payment went through — required then, refused
     * otherwise (see {@link electronicProviderRuleHolds}). Must name an ACTIVE
     * provider of this venue; the server checks that, not the schema.
     */
    electronicsProviderId: zod_1.z.string().min(1).optional(),
    /** Tab-level discount in whole IQD, on top of any per-session discount. */
    discountIqd: zod_1.z.number().int().min(0).default(0),
    /**
     * A percentage off the WHOLE party, applied at the till (owner request).
     *
     * The buckets above discount one class of thing each — play, cafe, kitchen —
     * because that is how a customer's standing arrangement is written. This is
     * the other conversation, the one that happens at the counter: "give them ten
     * percent off the lot".
     *
     * Taken off what is left AFTER the buckets, not off the raw subtotal. Applying
     * both to the same base would let a customer with a 20% cafe arrangement and a
     * 10% counter discount pay 70% of their coffee while the paper says two
     * discounts were given — arithmetic no cashier could defend at the counter.
     *
     * Whole percent: half a percent of a coffee is not a thing anyone haggles, and
     * an integer is what the receipt can print without a rounding footnote. It
     * ADDS to `discountIqd` rather than replacing it, so "10% and another 2,000
     * off" is one checkout.
     */
    discountPercent: zod_1.z.number().int().min(0).max(100).default(0),
    /**
     * The cashier takes the venue's service charge off THIS checkout (owner,
     * 2026-09-23: "the cashier could remove the service for any customer").
     *
     * THREE STATES, and the server reads all three: `true` waives it (audited),
     * `false` charges the venue's rate, and ABSENT charges NOTHING. The current
     * till always sends a boolean; only a staff tab still running a build from
     * before the service charge omits it, and that till quoted the customer a
     * total without the charge — money must match the quote, so it settles
     * uncharged and the server logs and audits the stale till. Deliberately no
     * `.default()`, which would erase the difference, and no percent the till
     * could echo back: a till can only ever say "none today", never invent a
     * different rate.
     */
    serviceChargeWaived: zod_1.z.boolean().optional(),
    /**
     * Per-bucket discounts entered at the till (SPEC §5.6 split discounts).
     *
     * Omitted entirely means "just the customer's saved percents", so every caller
     * that predates this — and every checkout where staff touch nothing — behaves
     * exactly as before. Unlike `discountIqd`, which spreads across every bill and
     * is judged against the grand total, each of these belongs to one bucket and
     * cannot exceed it.
     */
    buckets: zod_1.z
        .object({
        time: bucketDiscountSchema,
        cafe: bucketDiscountSchema,
        kitchen: bucketDiscountSchema,
        shisha: bucketDiscountSchema,
    })
        .optional(),
    /** Cash handed over, for change calculation. Ignored for account balance. */
    /**
     * ACCOUNT_BALANCE only: refuse unless at least this much REMAINS after the
     * debit. The portal shop passes its reserve here so the condition is part
     * of the conditional update itself — a concurrent charge can never slip a
     * purchase under the floor. Staff checkouts and sign-out settlement omit
     * it, so they can drain the balance to zero as they always have.
     */
    minRemainingBalanceIqd: zod_1.z.number().int().min(0).optional(),
    tenderedIqd: zod_1.z.number().int().min(0).optional(),
    /** Rounds for any round-billed device still open, keyed by session id. */
    rounds: zod_1.z
        .record(zod_1.z.string(), zod_1.z.object({
        singleRounds: zod_1.z.number().int().min(0).max(999).default(0),
        doubleRounds: zod_1.z.number().int().min(0).max(999).default(0),
        threeBallRounds: zod_1.z.number().int().min(0).max(999).default(0),
    }))
        .default({}),
    /**
     * Partial settlement — the "one friend leaves" case (SPEC §5.3).
     *
     * A party shares one tab. When one of them comes to the till, the cashier
     * asks which device was theirs and which cafe items they had, settles exactly
     * those, and the rest stays open under the same name for the others.
     *
     * OMITTED settles the whole tab: every session on it that has no bill yet,
     * plus every OPEN order bill. On a tab nobody has split that is precisely
     * what this endpoint has always done.
     *
     * ONE optional container rather than two sibling optional arrays, on purpose:
     * with siblings, `{ sessionIds: ['s1'] }` is ambiguous — does the missing
     * `orderBillIds` mean "no orders" or "all orders"? A container makes "whole
     * tab" exactly one state, unreachable by accident.
     *
     * An EMPTY selection is refused rather than read as "everything": on a till,
     * "settle nothing" and "settle the whole party" must not be one keystroke
     * apart. `rounds` entries for sessions outside the selection are ignored.
     */
    selection: zod_1.z
        .object({
        sessionIds: selectionIds.default([]),
        orderBillIds: selectionIds.default([]),
    })
        .refine((value) => value.sessionIds.length + value.orderBillIds.length > 0, {
        message: 'Select at least one device or order to settle.',
        path: ['sessionIds'],
    })
        .optional(),
})
    .refine(electronicProviderRuleHolds, {
    message: exports.ELECTRONIC_PROVIDER_RULE_MESSAGE,
    path: ['electronicsProviderId'],
});
//# sourceMappingURL=checkout.js.map